<template>
  <div class="attendants-day-wrap bg-white">
    <!-- <div class="controls bg-white">
      <div class="date">Agenda por Atendente: {{ today }}</div>
    </div> -->

    <div class="grid-wrap">
      <div
        class="grid"
        :style="{ gridTemplateColumns: gridTemplateColumns, gridTemplateRows: gridTemplateRows }"
      >
        <!-- header: empty cell then attendants names -->
        <div class="header time-header bg-white">Horário</div>
        <div v-for="a in attendants" :key="a.id" class="header attendant-header bg-white">
          <span class="attendant-name">{{ a.name }}</span>
          <span class="attendant-summary">
            <span class="attendant-service-count">{{ attendantStats[a.id]?.services || 0 }}</span>
            <span>{{ formatDuration(attendantStats[a.id]?.minutes || 0) }}</span>
          </span>
        </div>

        <!-- time labels in first column -->
        <template v-for="(slot, i) in slots" :key="`time-${i}`">
          <div class="time-cell bg-white" :style="{ gridColumn: '1 / 2', gridRow: (i + 2) + ' / ' + (i + 3) }">{{ slot }}</div>
        </template>

        <!-- empty slot cells (clickable) -->
        <template v-for="(a, ai) in attendants" :key="a.id">
          <template v-for="(slot, si) in slots" :key="`cell-${a.id}-${si}`">
            <div
              class="slot-cell bg-white"
              :style="{ gridColumn: (ai + 2) + ' / ' + (ai + 3), gridRow: (si + 2) + ' / ' + (si + 3) }"
              @click="handleSlotClick(a.id, slotsStartTimes[si])"
            ></div>
          </template>
        </template>

        <!-- appointments placed using grid-row and calculated height -->
        <div
          v-for="appt in mappedAppointments"
          :key="`appt-${appt.id}`"
          class="appointment"
          :class="{ 'blocked': appt.raw.status === 'BLOCKED' }"
          :style="{ gridColumn: appt.gridColumn, gridRow: appt.gridRow, height: appt.heightPx + 'px' }"
          @click.stop="onAppointmentClick(appt)">
          <div class="appt-time"><i class="flaticon appt-icon" :class="{'flaticon-check': appt.raw.confirmed, 'flaticon-time': !appt.raw.confirmed}"></i>{{ appt.start }} - {{ appt.end }}</div>
          <div class="appt-client"><i class="flaticon flaticon-user appt-icon"></i>{{ appt.clientName || 'Sem cliente' }}</div>
          <div class="appt-service"><i class="flaticon flaticon-sugar-cubes appt-icon"></i>{{ appt.serviceName || appt.title || 'Sem serviço' }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: "AttendantsDayView",
  props: {
    attendants: { type: Array, default: () => [] },
    appointments: { type: Array, default: () => [] },
    date: { type: String, default: new Date().toISOString().slice(0, 10) },
    today :{ type: String, default: () => "" }
  },
  data() {
    return {
      slotMinutes: 30,
    };
  },
  computed: {
    ...mapGetters(['getOperatingHours']),
    slotsStartTimes() {
      const operatingHours = this.getOperatingHours || { opening_time: '08:00', closing_time: '21:00' };
      const times = [];

      // Parse opening and closing times
      const [openHour, openMinute] = operatingHours.opening_time.split(':').map(Number);
      const [closeHour, closeMinute] = operatingHours.closing_time.split(':').map(Number);

      // Convert to minutes since midnight
      const openMinutes = openHour * 60 + openMinute;
      const closeMinutes = closeHour * 60 + closeMinute;

      // Generate slots only within operating hours
      for (let minutes = openMinutes; minutes < closeMinutes; minutes += this.slotMinutes) {
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        const hh = String(h).padStart(2, "0");
        const mm = String(m).padStart(2, "0");
        times.push(`${hh}:${mm}`);
      }

      return times;
    },
    slots() {
      return this.slotsStartTimes.map((t) => t);
    },
    gridTemplateColumns() {
      const cols = ["70px"];
      for (let i = 0; i < this.attendants.length; i++) cols.push("minmax(155px, 1fr)");
      return cols.join(" ");
    },
    gridTemplateRows() {
      // header row + 48 slots
      const rows = ["54px"];
      for (let i = 0; i < this.slots.length; i++) rows.push("40px");
      return rows.join(" ");
    },
    attendantStats() {
      const stats = {};

      for (const attendant of this.attendants) {
        stats[attendant.id] = {
          services: 0,
          minutes: 0,
        };
      }

      for (const appointment of this.appointments) {
        if (this.isBlockedOrCanceled(appointment)) continue;

        const attendantId = appointment.attendantId;
        if (!Object.prototype.hasOwnProperty.call(stats, attendantId)) continue;

        stats[attendantId].services += 1;
        stats[attendantId].minutes += this.getAppointmentDurationMinutes(appointment);
      }

      return stats;
    },
    mappedAppointments() {
      // map appointments to grid positions
      const map = [];
      const operatingHours = this.getOperatingHours || { opening_time: '08:00', closing_time: '21:00' };

      // Parse opening and closing times to calculate boundaries in minutes
      const [openHour, openMinute] = operatingHours.opening_time.split(':').map(Number);
      const openMinutes = openHour * 60 + openMinute;

      const [closeHour, closeMinute] = operatingHours.closing_time.split(':').map(Number);
      const closeMinutes = closeHour * 60 + closeMinute;

      // Helper to convert time string to minutes from start of day
      const timeToMinutes = (time) => {
        const [hh, mm] = time.split(":").map(Number);
        return hh * 60 + mm;
      };

      // Helper to calculate slot index for grid positioning
      const slotIndex = (appointmentMinutes) => {
        const relativeMinutes = appointmentMinutes - openMinutes;
        return Math.max(0, Math.floor(relativeMinutes / this.slotMinutes));
      };

      // Each slot is 40px and represents 30 minutes
      // So each minute = 40/30 = 1.333px
      const pixelsPerMinute = 40 / this.slotMinutes;

      for (const appt of this.appointments) {
        const aIndex = this.attendants.findIndex((a) => a.id === appt.attendantId);
        if (aIndex === -1) continue;

        // Retrieve unconstrained start and end times
        const originalStartMinutes = timeToMinutes(appt.start);
        const originalEndMinutes = timeToMinutes(appt.end);

        // Exclude appointments fully outside operating hours
        if (originalStartMinutes >= closeMinutes || originalEndMinutes <= openMinutes) {
          continue;
        }

        // Constrain start and end times to within operating hours
        const startMinutes = Math.max(openMinutes, originalStartMinutes);
        const endMinutes = Math.min(closeMinutes, originalEndMinutes);

        // Calculate duration in minutes (ensure at least 1 minute)
        const durationMinutes = Math.max(1, endMinutes - startMinutes);

        // Calculate pixel-based height based on constrained duration
        const heightPx = durationMinutes * pixelsPerMinute;

        // Position using constrained start time for the slot
        const startSlot = slotIndex(startMinutes);
        const gridRow = `${startSlot + 2}`;
        const gridColumn = `${aIndex + 2} / ${aIndex + 3}`;

        map.push({ ...appt, gridRow, gridColumn, heightPx });
      }
      return map;
    },
  },
  methods: {
    formatDate(d) {
      const t = new Date(d);
      return t.toLocaleDateString();
    },
    handleSlotClick(attendantId, time) {
      this.$emit("slot-click", { attendantId, time });
    },
    onAppointmentClick(appt) {
      this.$emit("appointment-click", appt);
    },
    isBlockedOrCanceled(appointment) {
      const status = String(appointment && appointment.raw && appointment.raw.status ? appointment.raw.status : "").toUpperCase();
      return ["BLOCKED", "CANCELED", "CANCELLED", "CANCELADO", "CANCELADA"].includes(status);
    },
    getAppointmentDurationMinutes(appointment) {
      const startMinutes = this.timeToMinutes(appointment && appointment.start);
      const endMinutes = this.timeToMinutes(appointment && appointment.end);

      if (startMinutes === null || endMinutes === null || endMinutes <= startMinutes) {
        return 0;
      }

      return endMinutes - startMinutes;
    },
    timeToMinutes(value) {
      if (!value) return null;
      const match = String(value).match(/(\d{1,2}):(\d{2})/);
      if (!match) return null;

      const hours = Number(match[1]);
      const minutes = Number(match[2]);
      if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;

      return hours * 60 + minutes;
    },
    formatDuration(totalMinutes) {
      const minutes = Number(totalMinutes || 0);
      if (minutes <= 0) return "0h";
      if (minutes < 60) return `${minutes}min`;

      const hours = Math.floor(minutes / 60);
      const remainder = minutes % 60;
      return remainder ? `${hours}h ${remainder}min` : `${hours}h`;
    },
  },
};
</script>

<style scoped>
.attendants-day-wrap {
  border: 1px solid #eee;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}
.controls {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.grid-wrap {
  overflow: auto;
  max-height: calc(100vh - 200px);
  position: relative; /* ensures sticky positioning reference */
}
.grid {
  display: grid;
  align-items: stretch;
}
.header {
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
  padding: 12px;
  font-weight: 600;
  text-align: left;
}
.time-header {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 90; /* below header (which has z-index: 99) but above other slots */
  background: #fafafa;
  box-shadow: 2px 0 5px rgba(0,0,0,0.05);
}

/* keep first column (time labels) visible during horizontal scrolling */
.time-cell {
  position: sticky;
  left: 0;
  background: #fff; /* match grid background to avoid bleed through */
  z-index: 89; /* below header but above other slots */
  box-shadow: 2px 0 5px rgba(0,0,0,0.03);
}
.attendant-header {
  padding-left: 16px;
  position: sticky;
  top: 0;
  z-index: 80; /* below time cells to prevent overlap */ /* below time cells to prevent overlap */
}
.attendant-name {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.attendant-summary {
  align-items: center;
  color: #8b8a9a;
  display: flex;
  font-size: 11px;
  font-weight: 500;
  gap: 5px;
  line-height: 1.2;
  margin-top: 3px;
  white-space: nowrap;
}
.attendant-service-count {
  align-items: center;
  background: rgba(101, 96, 240, 0.1);
  border: 1px solid rgba(101, 96, 240, 0.18);
  border-radius: 50%;
  color: #5651d9;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 10px;
  font-weight: 800;
  height: 17px;
  justify-content: center;
  line-height: 1;
  width: 17px;
}
.time-cell {
  padding: 8px 12px;
  border-bottom: 1px solid #f5f5f5;
  color: #777;
}
.slot-cell {
  border-left: 1px solid #f5f5f5;
  border-bottom: 1px solid #f5f5f5;
  min-height: 36px;
  background: transparent;
  z-index: 1;
}
.appointment {
  background: #3b82f6;
  color: #fff;
  padding: 6px 8px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 1px 2px rgba(0,0,0,0.06);
  z-index: 50;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-height: 30px;
  align-self: start;
}
.appointment.blocked {
  background: #ef4444;
  border: 1px solid rgba(255, 255, 255, 0.3);
}
.appt-time {
  font-size: 11px;
  opacity: 0.9;
}
.appt-client {
  font-weight: 600;
  font-size: 13px;
  margin-top: 2px;
}
.appt-service {
  font-size: 12px;
  opacity: 0.9;
  margin-top: 2px;
}
.appt-icon {
  margin-right: 4px;
  font-size: 11px;
  vertical-align: middle;
}

  /* Mobile: remove borders and use full width to avoid side padding/borders */
  @media (max-width: 767px) {
    .attendants-day-wrap {
      border: none;
      border-radius: 0;
      display: flex;
      min-height: 0;
      width: 100%;
      flex-direction: column;
    }

    /* ensure the grid fills the container and scrolls properly */
    .grid-wrap {
      flex: 1 1 auto;
      min-height: 0;
      height: 100%;
      max-height: none;
      padding: 0;
      overscroll-behavior: contain;
    }
  }
</style>

<style>
body.dark .attendants-day-wrap .attendant-summary {
  color: #bcbacb !important;
}

body.dark .attendants-day-wrap .attendant-service-count {
  background: rgba(101, 96, 240, 0.2) !important;
  border-color: rgba(143, 140, 255, 0.32) !important;
  color: #c8c6ff !important;
}
</style>
